import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { opsAdmin, type PendingChange } from "@/api/opsAdmin";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { Button, Card, CardBody, EmptyState, FormError, Input, Label } from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Cooling-off pending changes — risk-affecting settings changes that clients
 * scheduled and that apply after a mandatory cooling-off window. Operators can
 * cancel one before it applies (fraud / mistake / support request).
 */
export function AdminPendingChangesPage() {
  const list = useQuery({
    queryKey: ["pending-changes"],
    queryFn: () => opsAdmin.pendingChanges(true),
  });
  const [cancelling, setCancelling] = useState<PendingChange | null>(null);

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-base font-semibold text-term-fg">Cooling-off changes</h1>
        <p className="text-xs text-term-mute">
          Scheduled settings changes awaiting their cooling-off window.
        </p>
      </div>
      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="Nothing pending" description="Scheduled risk-affecting changes appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Applies</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">User</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Field</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Change</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c) => (
                  <tr key={c.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {c.applies_at ? new Date(c.applies_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {c.user_id ? c.user_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      <span className="text-term-fg">{c.field}</span>
                      <div className="text-term-mute">{c.target_table}</div>
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      <span className="line-through">{c.old_value ?? "—"}</span> →{" "}
                      <span className="text-term-fg">{c.new_value ?? "—"}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <Button variant="danger" onClick={() => setCancelling(c)}>
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {cancelling && <CancelModal change={cancelling} onClose={() => setCancelling(null)} />}
    </div>
  );
}

function CancelModal({ change, onClose }: { change: PendingChange; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => opsAdmin.cancelPendingChange(change.id, reason || undefined),
    onSuccess: () => {
      toast.push("Change cancelled", "warn");
      qc.invalidateQueries({ queryKey: ["pending-changes"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="Cancel scheduled change">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="rounded-sm border border-term-border bg-term-elev px-2 py-1.5 text-xs text-term-dim">
          <span className="text-term-mute">{change.field}</span>:{" "}
          {change.old_value ?? "—"} → <span className="text-term-fg">{change.new_value ?? "—"}</span>
        </div>
        <div>
          <Label>Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="optional" />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Keep
          </Button>
          <Button type="submit" variant="danger" loading={submit.isPending}>
            Cancel change
          </Button>
        </div>
      </form>
    </Modal>
  );
}
